import Table from "@/common/components/Table"
import { Pagination } from "@/common/components/pagination"
import { KeyValueObj } from "@/common/types"
import { useState, useMemo } from "react"
import { Button } from "@/common/components/button"
import { ClusterWrapper, ModalWrapper } from "../admin/cluster/style"
import { TableHeaders, TableKeys, TableData, getFormInputs } from "./subs/data"
import useQuery from "@/hooks/useQuery"
import { COMPANY_TYPES_URL, PRODUCT_URL, DISCOUNTS_URL } from "@/common/constants"
import { Modal } from "@/common/components/modal"
import success from "@/assets/images/success_alt.png"
import { useApiCallHandling } from "@/hooks/useApiCallHandling"
import { useNotification } from "@/common/components/Notifications/NotificationProvider"
import Form from "@/common/components/form/Form"
import { ActionPopup } from "@/common/components/action-popup"
import { usePositioning } from "@/hooks/usePositioning"

enum EModal {
  SET_DISCOUNTS = "set_discounts",
  EDIT_DISCOUNTING = "edit_discounting",
  DELETE_DISCOUNTING = "delete_discounting",
  SUCCESS = "success",
}

interface DiscountingData {
  id: string | number
  product?: {
    name: string
    displayName?: string
  }
  company_type?: {
    title: string
    name?: string
  }
  minimum_quantity?: string | number
  minimum_order_quantity?: string | number
  discount_percentage?: string | number
}

export function Discounts() {
  const [activeModal, setActiveModal] = useState<EModal | null>(null)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [selectedDiscounting, setSelectedDiscounting] = useState<DiscountingData | null>(null)
  const [showActions, setShowActions] = useState(false)
  const [searching, setSearching] = useState(false)
  const [setCordinate, cordinate] = usePositioning()

  const [pagination, setPagination] = useState<KeyValueObj>({
    page: 1,
    per_page: 10,
  })

  const makeRequest = useApiCallHandling()
  const notify = useNotification()

  const { data: companyTypesData, loading: companyTypesLoading } = useQuery(COMPANY_TYPES_URL)
  const companyTypes = companyTypesData || []

  const { data: productsResponse, loading: productsLoading } = useQuery(PRODUCT_URL)

  const products = useMemo(() => {
    const productData = productsResponse?.data || productsResponse || []
    return productData.map((product: any) => ({
      ...product,
      name: product.brand_name || product.name || product.title || `Product ${product.id}`,
      displayName: `${product.brand_name || "Unknown"} - ${product.medex_code || product.id}`,
    }))
  }, [productsResponse])

  const {
    data: discountingResponse,
    loading: discountingLoading,
    meta: discountingMeta,
    refetch: refetchDiscounting,
  } = useQuery(`${DISCOUNTS_URL}?page=${pagination.page}&per_page=${pagination.per_page}`)

  const discountingData = discountingResponse?.data || discountingResponse || []

  const enrichedDiscountingData = useMemo(() => {
    if (!discountingData || !Array.isArray(discountingData)) return []

    return discountingData.map((discounting: any) => {
      const matchedProduct = products.find((p: any) => p.id === discounting.product?.id)

      return {
        ...discounting,
        product: {
          ...discounting.product,
          name:
            matchedProduct?.name ||
            matchedProduct?.title ||
            matchedProduct?.product_name ||
            discounting.product?.name ||
            "Unknown Product",
        },
      }
    })
  }, [discountingData, products])

  const updateDiscountTableData = (url: string) => {
    setSearching(true)
    refetchDiscounting(url)
  }

  const handleSearch = (value: string) => {
    const params = new URLSearchParams({
      page: String(pagination.page),
      per_page: String(pagination.per_page),
      search_term: value,
    })

    updateDiscountTableData(`${DISCOUNTS_URL}?${params.toString()}`)
  }

  const handleFilter = (values: KeyValueObj) => {
    const params = new URLSearchParams({
      page: String(pagination.page),
      per_page: String(pagination.per_page),
      ...values,
    })

    updateDiscountTableData(`${DISCOUNTS_URL}?${params.toString()}`)
  }

  const handleDropdownClick = (e: any, rowId: string | number) => {
    setCordinate(e)
    const discounting = enrichedDiscountingData.find((d: any) => d.id === rowId)
    setSelectedDiscounting(discounting || null)
    setShowActions(true)
  }

  const handleEditAction = () => {
    setActiveModal(EModal.EDIT_DISCOUNTING)
    setShowActions(false)
  }

  const handleDeleteAction = () => {
    setActiveModal(EModal.DELETE_DISCOUNTING)
    setShowActions(false)
  }

  const handleSubmit = (formValues: any) => {
    setSubmitLoading(true)

    const payload = {
      discount_bands: [
        {
          product_id: formValues.product_id || null,
          company_type_id: formValues.company_type_id || null,
          minimum_quantity: formValues.minimum_order_quantity ? parseInt(formValues.minimum_order_quantity) : null,
          discount_percentage: formValues.discount_percentage ? parseFloat(formValues.discount_percentage) : null,
        },
      ],
    }

    if (!payload.discount_bands[0].product_id) {
      notify({
        message: "Product selection is required",
        type: "ERROR",
      })
      setSubmitLoading(false)
      return
    }

    makeRequest(
      DISCOUNTS_URL,
      "POST",
      payload,
      (_) => {
        setActiveModal(EModal.SUCCESS)
        refetchDiscounting()
      },
      (error) => {
        notify({
          message: error.response?.data?.message || "Error creating discount",
          type: "ERROR",
        })
      },
      () => {
        setSubmitLoading(false)
      },
      { dontNotifyOnSucess: true, dontNotifyonFail: true },
    )
  }

  const handleEdit = (formValues: any) => {
    setSubmitLoading(true)

    const payload = {
      minimum_quantity: formValues.minimum_order_quantity ? parseInt(formValues.minimum_order_quantity) : null,
      discount_percentage: formValues.discount_percentage ? parseFloat(formValues.discount_percentage) : null,
    }

    makeRequest(
      `${DISCOUNTS_URL}/${selectedDiscounting?.id}`,
      "PUT",
      payload,
      (_) => {
        setActiveModal(EModal.SUCCESS)
        refetchDiscounting()
        setSelectedDiscounting(null)
      },
      (error) => {
        notify({
          message: error.response?.data?.message || "Error updating discounting",
          type: "ERROR",
        })
      },
      () => {
        setSubmitLoading(false)
      },
      { dontNotifyOnSucess: true, dontNotifyonFail: true },
    )
  }

  const handleDelete = () => {
    if (!selectedDiscounting || !selectedDiscounting.id) {
      notify({ message: "No discounting item selected for deletion", type: "ERROR" })
      return
    }

    setSubmitLoading(true)

    makeRequest(
      `${DISCOUNTS_URL}/${selectedDiscounting.id}`,
      "DELETE",
      {},
      () => {
        refetchDiscounting()
        setActiveModal(EModal.SUCCESS)
        setSelectedDiscounting(null)
        notify({ message: "Discount deleted successfully", type: "SUCCESS" })
      },
      (error) => {
        if (!error?.response || [200, 204].includes(error?.response?.status)) {
          refetchDiscounting()
          setActiveModal(EModal.SUCCESS)
          setSelectedDiscounting(null)
          notify({ message: "Discount deleted successfully", type: "SUCCESS" })
          return
        }

        notify({
          message: error.response?.data?.message || error.message || "Error deleting discount",
          type: "ERROR",
        })
        setActiveModal(null)
        setSelectedDiscounting(null)
      },
      () => {
        setSubmitLoading(false)
      },
      { dontNotifyOnSucess: true, dontNotifyonFail: true },
    )
  }

  const tableDataWithActions = useMemo(() => {
    return TableData(enrichedDiscountingData).map((row: any) => ({
      ...row,
      action: {
        type: "button",
        buttonType: "dots",
        buttonLoading: false,
        buttonName: "",
        onClick: handleDropdownClick,
      },
    }))
  }, [enrichedDiscountingData])

  return (
    <ClusterWrapper>
      <div className="add-button">
        <Button onClick={() => setActiveModal(EModal.SET_DISCOUNTS)}>Set Price Discounts</Button>
      </div>

      <ActionPopup
        position={cordinate}
        open={showActions}
        disabled={submitLoading}
        actions={[
          { label: "Edit", action: handleEditAction },
          { label: "Delete", action: handleDeleteAction },
        ]}
        onClose={() => setShowActions(false)}
      />

      <div className="table w-full">
        <Table
          tableHeaders={[...TableHeaders, ""]}
          tableData={tableDataWithActions}
          tableKeys={[...TableKeys, "action"]}
          tableTitle="Price Discounts"
          tableSubtitle={`${discountingMeta?.total_count || enrichedDiscountingData.length || 0} discount items`}
          loading={discountingLoading || searching}
          spaced
          rowLineColor="light"
          handleSearch={handleSearch}
          handleFilter={handleFilter}
        />

        <Pagination
          pageCount={discountingMeta?.total_pages || 1}
          onNextPage={(event) => setPagination({ ...pagination, page: event.selected + 1 })}
          onPerPageChange={(event) =>
            setPagination({
              ...pagination,
              per_page: Number(event?.target?.value),
              page: 1,
            })
          }
        />
      </div>

      <Modal
        title="Set Discount"
        isOpen={activeModal === EModal.SET_DISCOUNTS}
        close={() => {
          setActiveModal(null)
          setSelectedDiscounting(null)
        }}
      >
        <div className="modal max-w-[600px] md:w-[600px] min-h-[300px] bg-white">
          <Form
            formInputs={getFormInputs(null, products, productsLoading, companyTypes, companyTypesLoading)}
            processInputs={handleSubmit}
            loadingState={submitLoading}
          />
        </div>
      </Modal>

      <Modal
        title="Edit Discounts"
        isOpen={activeModal === EModal.EDIT_DISCOUNTING}
        close={() => {
          setActiveModal(null)
          setSelectedDiscounting(null)
        }}
      >
        <div className="modal max-w-[600px] md:w-[600px] min-h-[300px] bg-white">
          <Form
            formInputs={getFormInputs(
              selectedDiscounting,
              products,
              productsLoading,
              companyTypes,
              companyTypesLoading,
            )}
            processInputs={handleEdit}
            loadingState={submitLoading}
          />
        </div>
      </Modal>

      <Modal
        title="Delete Discounts"
        isOpen={activeModal === EModal.DELETE_DISCOUNTING}
        close={() => {
          setActiveModal(null)
          setSelectedDiscounting(null)
        }}
      >
        <ModalWrapper>
          <p className="text-center mb-6">
            Are you sure you want to delete this discount item?
            <br />
            <span className="font-semibold mt-2 block">This action cannot be undone.</span>
          </p>

          <div className="flex gap-3 justify-center">
            <Button
              onClick={() => {
                setActiveModal(null)
                setSelectedDiscounting(null)
              }}
              disabled={submitLoading}
            >
              Cancel
            </Button>
            <Button loading={submitLoading} disabled={submitLoading} onClick={handleDelete}>
              Delete
            </Button>
          </div>
        </ModalWrapper>
      </Modal>

      <Modal isOpen={activeModal === EModal.SUCCESS} close={() => setActiveModal(null)}>
        <ModalWrapper>
          <img src={success} alt="success" />
          <p>Discount set Successfully {selectedDiscounting ? "Updated/Deleted" : "Created"}</p>

          <Button onClick={() => setActiveModal(null)}>Done</Button>
        </ModalWrapper>
      </Modal>
    </ClusterWrapper>
  )
}
